import React, { useRef } from 'react';

import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import {
  facebookShareURL,
  lineShareURL,
} from '@/components/shared/utility/share';
import { styled } from '@/stitches.config';
import { IThemeLCV } from '@/types/shared';

const StyledSocialMediaShare = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$space-2',
  '@lg': {
    justifyContent: 'flex-start',
  },
  variants: {
    justify: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'center',
        '@md': {
          justifyContent: 'flex-end',
        },
      },
    },
  },
});

interface SocialProps
  extends React.ComponentProps<typeof StyledSocialMediaShare> {
  title: string;
  id: string;
  url: string;
  theme?: IThemeLCV;
  dataTrack?: string;
  dataTest?: string;
}

export const SocialMediaShareButtons: React.FC<SocialProps> = ({
  title,
  id,
  url,
  theme = 'light',
  dataTrack = 'social-share',
  dataTest = 'socialShare',
  ...rest
}: SocialProps) => {
  const buttonVariant = () => {
    switch (theme) {
      case 'light':
        return 'button-icon-color-gray';
      case 'dark':
        return 'button-icon-color-white';
      default:
        return 'button-icon-color-gray';
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const copyLink = async () => {
    if (inputRef.current) {
      inputRef.current.select();
      inputRef.current.setSelectionRange(0, 99999);
      document.execCommand('copy');
      alert('คัดลอกที่อยู่ของเว็บไซต์เรียบร้อยแล้ว');
    }
  };

  return (
    <StyledSocialMediaShare {...rest}>
      <ButtonIcon
        href={facebookShareURL(url)}
        target="_blank"
        tracking={{
          dataTrack: dataTrack,
          dataTrackText: 'facebook-icon',
          dataTrackUrl: facebookShareURL(url),
          dataTrackValue: title,
        }}
        test={{
          dataTest: `${dataTest}_facebook`,
        }}
        icon="facebook"
        variant={buttonVariant()}
        size="md"
        border
      />
      <ButtonIcon
        href={lineShareURL(url)}
        target="_blank"
        tracking={{
          dataTrack: dataTrack,
          dataTrackText: 'line-icon',
          dataTrackUrl: lineShareURL(url),
          dataTrackValue: title,
        }}
        test={{
          dataTest: `${dataTest}_line`,
        }}
        icon="line"
        variant={buttonVariant()}
        size="md"
        border
      />
      <ButtonIcon
        variant={buttonVariant()}
        size="md"
        border
        onClick={copyLink}
        tracking={{
          dataTrack: dataTrack,
          dataTrackText: 'copy-icon',
          dataTrackUrl: url,
          dataTrackValue: title,
        }}
        test={{
          dataTest: `${dataTest}_copyLink`,
        }}
      >
        <Icon icon="link" />
        <input
          id={id}
          ref={inputRef}
          type="text"
          style={{
            opacity: 0,
            position: 'absolute',
            zIndex: -1,
            overflow: 'hidden',
          }}
          value={url}
          readOnly
        />
        <label htmlFor={id}>
          <Text sr-only>Copy Link</Text>
        </label>
      </ButtonIcon>
    </StyledSocialMediaShare>
  );
};

SocialMediaShareButtons.displayName = 'SocialMediaShareButtons';
